// SPDX-License-Identifier: GPL-3.0-or-later
// The best score of this device. It is an adapter over the run save (`pinball.run.v1`, see
// run-save.ts). The page asks `record` at the end of each game and `recordStage` for each stage.
//
// Rule for the "new best" cue: the first record of a value has no earlier best to beat, so it only
// saves the value and gives no cue. Every score is a best on the first game, and that tells the
// player nothing. From the second record on, a value that is higher than the saved best is a new
// best. A value of 0 or less never counts.
//
// A storage that is blocked, full or read-only never breaks the game. A save of a newer version is
// read-only. The best stays in memory for this page. The adapter also writes the old key
// `pinball.best.v1`, so a rollback to an older build still reads the best.

import type { RunSave } from "../src/table/run/types.ts";
import {
  BEST_KEY,
  type BestStorage,
  cleanScore,
  createRunSaveStore,
  parseBestText,
  type RunSavePatch,
} from "./run-save.ts";

// The score helpers live in run-save.ts, so the two modules never import each other.
export { BEST_KEY, type BestStorage, cleanScore, parseBestText };

/** The result of one stage in a game. */
export interface StageResult {
  /** The 0-based index of the stage. */
  readonly index: number;
  /** The points of the stage in this game. */
  readonly points: number;
  /** The best points of the stage now. */
  readonly best: number;
  /** True when this game beat an earlier best of the stage. Never true on the first record. */
  readonly isNew: boolean;
}

/** The result of one game. */
export interface BestResult {
  /** The best score now. 0 when the player has no best yet. */
  readonly best: number;
  /** True when this score beat an earlier best. Never true on the first game. */
  readonly isNew: boolean;
  /** The stages that ran in this game, in play order. Missing when no run view is known. */
  readonly stages?: readonly StageResult[];
}

/** The best score of the device. */
export interface HighScore {
  /** The best score now. 0 when none. */
  best(): number;
  /** The save now, from memory. Read it after a write: a read-only storage gives defaults. */
  save(): RunSave;
  /** Record the score of a finished game. */
  record(score: number): BestResult;
  /**
   * Record the points of one stage (0-based index). `cleared` also unlocks the next stage at once.
   * It returns true when the points beat an earlier stage best above 0.
   */
  recordStage(index: number, points: number, cleared: boolean): boolean;
}

function writeBest(storage: BestStorage | null, score: number): void {
  try {
    storage?.setItem(BEST_KEY, String(score));
  } catch {
    // The storage is blocked or full. The best stays for this page.
  }
}

/** The max of each field of two saves. A save never loses progress. */
function maxSave(a: RunSave, b: RunSave): RunSave {
  return {
    v: 1,
    best: Math.max(a.best, b.best),
    unlocked: Math.max(a.unlocked, b.unlocked),
    stageBest: a.stageBest.map((value, i) => Math.max(value, b.stageBest[i] ?? 0)),
    clears: Math.max(a.clears, b.clears),
  };
}

/**
 * Make the best score keeper. It reads the run save now. `onSave` runs after each write with the
 * save in memory (the page draws the level select from it).
 */
export function createHighScore(
  storage: BestStorage | null,
  onSave?: (save: RunSave) => void,
): HighScore {
  const store = createRunSaveStore(storage);
  let mem = store.read();

  /** Join the storage into memory. Another tab can have written since the last read. */
  const refresh = (): RunSave => {
    mem = maxSave(mem, store.read());
    return mem;
  };
  const commit = (patch: RunSavePatch): void => {
    mem = maxSave(mem, store.write(patch).save);
    onSave?.(mem);
  };

  return {
    best: () => mem.best,
    save: () => mem,
    record(score) {
      const clean = cleanScore(score);
      const earlier = refresh().best;
      if (clean <= earlier) return { best: earlier, isNew: false };
      commit({ best: clean });
      writeBest(storage, clean);
      return { best: clean, isNew: earlier > 0 };
    },
    recordStage(index, points, cleared) {
      const clean = cleanScore(points);
      const earlier = refresh().stageBest[index] ?? 0;
      const better = clean > earlier;
      if (!better && !cleared) return false;
      commit({
        ...(cleared ? { clearedStage: index + 1 } : {}),
        ...(better ? { stagePoints: { index, points: clean } } : {}),
      });
      return better && earlier > 0;
    },
  };
}
